/* eslint-disable @typescript-eslint/no-explicit-any */

import { Box } from '@mui/material';
import {
 Dispatch,
 MutableRefObject,
 SetStateAction,
 useEffect,
 useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';

import { usePage } from '../../../contexts/usePageContext';
import { PageNameType } from '../../../redux/clientConfiguration/clientConfigurationSlice';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import reduxSlicesList from '../../../redux/reduxSlicesList';
import { RootState } from '../../../redux/store';
import { CustomGridApi, TableModalState } from '../XTable/types';
import EditArray from './EditArray';
import EditForm from './EditForm';
import EditTabs from './EditTabs';

const EditTabsWrapper = ({
 handleClose,
 modalState,
 setModalState,
 setCreateModalOpen,
 isOpen,
 apiRef,
 contentRef,
 itemData,
}: {
 handleClose: () => void;
 modalState: TableModalState | undefined;
 setModalState: Dispatch<SetStateAction<TableModalState>>;
 setCreateModalOpen: Dispatch<SetStateAction<boolean>>;
 isOpen: boolean;
 apiRef: MutableRefObject<CustomGridApi>;
 contentRef: React.RefObject<HTMLDivElement | null>;
 itemData?: any;
}) => {
 const dispatch = useAppDispatch();
 const isCreate = modalState?.action === 'create';
 const { t } = useTranslation();
 const { pageName } = usePage();
 const slice = reduxSlicesList[pageName as keyof typeof reduxSlicesList];

 const selector = useAppSelector(
  (state: RootState) => state[pageName as PageNameType],
 );
 const { item, structure, success, loading } = selector;

 useEffect(() => {
  if (!isCreate && modalState) {
   dispatch(slice.getItemStart({ table: pageName, id: modalState.id }));
  }
 }, [modalState, isOpen]);

 const editFormElement = useMemo(
  () => (
   <EditForm
    handleClose={handleClose}
    modalState={modalState}
    setModalState={setModalState}
    setCreateModalOpen={setCreateModalOpen}
    isOpen={isOpen}
    apiRef={apiRef}
    activeTabName={'main'}
   />
  ),
  [modalState],
 );

 const tableArrays = useMemo(
  () =>
   structure?.filter((field) => field.Type === 'array' && field.IsEditable),
  [structure, item],
 );

 const tabNames = useMemo(() => {
  const tabs = new Set<string>();
  structure?.forEach((field) => {
   if (field.Group) {
    const groupPattern = /^(.+?)_(.+)$/;
    const match = field.Group.match(groupPattern);
    if (match) {
     const [, tabName] = match;
     tabs.add(tabName);
    }
   }
  });
  return Array.from(tabs);
 }, [structure]);

 const tablesTabs = useMemo(
  () => [
   {
    key: t('entities.data'),
    content: editFormElement,
   },
   ...tabNames.map((tabName) => ({
    key: tabName,
    content: (
     <EditForm
      handleClose={handleClose}
      modalState={modalState}
      setModalState={setModalState}
      setCreateModalOpen={setCreateModalOpen}
      isOpen={isOpen}
      apiRef={apiRef}
      activeTabName={tabName}
     />
    ),
   })),
   ...(tableArrays || []).map((field) => ({
    key: field.Translation,
    count: item?.[field.Name]?.length || 0,
    content: (
     <EditArray arrayKey={field.Name} arrayTranslation={field.Translation} />
    ),
   })),
  ],
  [
   tabNames,
   tableArrays,
   item,
   editFormElement,
   handleClose,
   modalState,
   setModalState,
   setCreateModalOpen,
   isOpen,
   apiRef,
  ],
 );

 return (
  <Box
   sx={{
    width: '100%',
   }}
  >
   <EditTabs
    tabs={tablesTabs}
    ariaLabel={`${t(`entities.${pageName}`)} information tabs`}
    defaultTab={modalState?.tab || 0}
   />
  </Box>
 );
};

export default EditTabsWrapper;
